import { useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { toast } from 'sonner';
import type { EventLocation, TrackingEventMetadata } from '../types';

/**
 * Custom Hook for Real-Time Shipment Tracking
 *
 * This hook establishes a WebSocket connection to receive real-time updates
 * for shipment positions, tracking events, ETAs, and temperature alerts.
 *
 * Phase 2 - Real-Time Updates
 */

export interface PositionUpdate {
  shipmentId: string;
  position: {
    id: string;
    latitude: number;
    longitude: number;
    heading?: number;
    speed?: number;
    altitude?: number;
    source: string;
    timestamp: string;
  };
  timestamp: string;
}

export interface TrackingEventUpdate {
  shipmentId: string;
  event: {
    id: string;
    eventType: string;
    eventCategory: string;
    location?: EventLocation;
    timestamp: string;
    description?: string;
    metadata?: TrackingEventMetadata;
  };
  timestamp: string;
}

export interface ETAUpdate {
  shipmentId: string;
  eta: {
    id: string;
    milestoneType: string;
    estimatedArrival: string;
    confidence?: number;
    source: string;
  };
  timestamp: string;
}

export interface TemperatureAlert {
  shipmentId: string;
  alert: {
    temperature: number;
    humidity?: number;
    reason: string;
    location?: EventLocation;
    timestamp: string;
  };
  timestamp: string;
}

export function useShipmentTracking(shipmentId: string | null) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [latestPosition, setLatestPosition] = useState<PositionUpdate | null>(null);
  const [latestEvent, setLatestEvent] = useState<TrackingEventUpdate | null>(null);
  const [latestETA, setLatestETA] = useState<ETAUpdate | null>(null);
  const [latestAlert, setLatestAlert] = useState<TemperatureAlert | null>(null);

  // Store previous shipmentId to detect changes
  const prevShipmentIdRef = useRef<string | null>(null);

  /**
   * Initialize WebSocket connection
   */
  useEffect(() => {
    // VITE_API_URL carries the /api prefix (see api/client.ts), but the socket
    // namespace hangs off the server origin — strip it before connecting.
    const apiUrl = (import.meta.env.VITE_API_URL || 'http://localhost:3001/api').replace(
      /\/api\/?$/,
      ''
    );
    const wsUrl = apiUrl.replace(/^http/, 'ws');

    // Create socket connection to /tracking namespace
    const socketInstance = io(`${wsUrl}/tracking`, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      auth: {
        token: localStorage.getItem('token'), // JWT token for authentication
      },
    });

    // Connection established
    socketInstance.on('connect', () => {
      console.log('WebSocket connected:', socketInstance.id);
      setIsConnected(true);
    });

    // Connection lost
    socketInstance.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
      setIsConnected(false);

      if (reason === 'io server disconnect') {
        // Server disconnected the socket, need to reconnect manually
        socketInstance.connect();
      }
    });

    // Connection error
    socketInstance.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      setIsConnected(false);
    });

    // Server messages
    socketInstance.on('connected', (data) => {
      console.log('Server message:', data.message);
    });

    socketInstance.on('error', (data) => {
      console.error('Server error:', data.message);
      toast.error(data.message);
    });

    // Position updates
    socketInstance.on('position_update', (data: PositionUpdate) => {
      console.log('Position update received:', data);
      setLatestPosition(data);
    });

    // Tracking events
    socketInstance.on('tracking_event', (data: TrackingEventUpdate) => {
      console.log('Tracking event received:', data);
      setLatestEvent(data);

      // Show toast notification for important events
      if (data.event.eventCategory === 'MILESTONE') {
        toast.success(`New milestone: ${data.event.eventType.replace('_', ' ')}`);
      } else if (data.event.eventCategory === 'EXCEPTION') {
        toast.error(`Exception: ${data.event.description || data.event.eventType}`);
      }
    });

    // ETA updates
    socketInstance.on('eta_update', (data: ETAUpdate) => {
      console.log('ETA update received:', data);
      setLatestETA(data);
      toast.info('Estimated arrival time updated');
    });

    // Temperature alerts
    socketInstance.on('temperature_alert', (data: TemperatureAlert) => {
      console.log('Temperature alert received:', data);
      setLatestAlert(data);
      toast.error(`Temperature alert: ${data.alert.reason}`, {
        description: `${data.alert.temperature}°C`,
      });
    });

    // Broadcast messages
    socketInstance.on('broadcast', (data) => {
      console.log('Broadcast message:', data.message);
      toast.info(data.message);
    });

    setSocket(socketInstance);

    // Cleanup on unmount
    return () => {
      console.log('Cleaning up WebSocket connection');
      socketInstance.disconnect();
    };
  }, []); // Only run once on mount

  /**
   * Subscribe/unsubscribe to shipment updates when shipmentId changes
   */
  useEffect(() => {
    if (!socket || !isConnected) return;

    // Unsubscribe from previous shipment
    if (prevShipmentIdRef.current) {
      console.log('Unsubscribing from shipment:', prevShipmentIdRef.current);
      socket.emit('unsubscribe_shipment', prevShipmentIdRef.current);
    }

    // Track listener for cleanup
    let handleSubscribed: ((data: { shipmentId: string; message: string }) => void) | null = null;

    // Subscribe to new shipment
    if (shipmentId) {
      console.log('Subscribing to shipment:', shipmentId);
      socket.emit('subscribe_shipment', shipmentId);

      // Listen for subscription confirmation
      handleSubscribed = (data: { shipmentId: string; message: string }) => {
        if (data.shipmentId === shipmentId) {
          console.log('Subscribed:', data.message);
          toast.success('Real-time tracking enabled');
        }
      };

      socket.on('subscribed', handleSubscribed);

      // Update ref
      prevShipmentIdRef.current = shipmentId;
    }

    // Cleanup: remove listener and unsubscribe when shipmentId changes or component unmounts
    return () => {
      // Always remove the listener if it was added
      if (handleSubscribed) {
        socket.off('subscribed', handleSubscribed);
      }
      if (shipmentId) {
        socket.emit('unsubscribe_shipment', shipmentId);
      }
    };
  }, [socket, isConnected, shipmentId]);

  /**
   * Manually reconnect to WebSocket
   */
  const reconnect = useCallback(() => {
    if (socket && !isConnected) {
      socket.connect();
    }
  }, [socket, isConnected]);

  /**
   * Get connection status
   */
  const getConnectionStatus = useCallback((): 'connected' | 'disconnected' | 'connecting' => {
    if (!socket) return 'disconnected';
    if (isConnected) return 'connected';
    return 'connecting';
  }, [socket, isConnected]);

  return {
    // Connection status
    isConnected,
    connectionStatus: getConnectionStatus(),

    // Latest updates
    latestPosition,
    latestEvent,
    latestETA,
    latestAlert,

    // Actions
    reconnect,

    // Socket instance (for advanced usage)
    socket,
  };
}
